import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import type { 
  EthereumWallet, 
  EthereumTransaction, 
  ServiceStatus 
} from '@/services/api/EthereumApiClient';

interface EthereumContextType {
  // State
  wallet: EthereumWallet | null;
  balance: {
    balanceEth: number;
    balanceUsd: number;
    ethPrice: number;
  } | null;
  transactions: EthereumTransaction[];
  serviceStatus: ServiceStatus | null;
  loading: boolean;
  error: string | null;

  // Actions
  createWallet: () => Promise<void>;
  importWallet: (data: { privateKey?: string; mnemonic?: string }) => Promise<void>;
  loadWallet: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  loadTransactions: (limit?: number, skip?: number) => Promise<void>;
  checkServiceStatus: () => Promise<void>;
}

const EthereumContext = createContext<EthereumContextType | undefined>(undefined);

export function useEthereum() {
  const context = useContext(EthereumContext);
  if (!context) {
    throw new Error('useEthereum must be used within EthereumProvider');
  }
  return context;
}

interface EthereumProviderProps {
  children: ReactNode;
}

export function EthereumProvider({ children }: EthereumProviderProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [wallet, setWallet] = useState<EthereumWallet | null>(null);
  const [balance, setBalance] = useState<{
    balanceEth: number;
    balanceUsd: number;
    ethPrice: number;
  } | null>(null);
  const [transactions, setTransactions] = useState<EthereumTransaction[]>([]);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load wallet and status when user changes
  useEffect(() => {
    if (user) {
      loadWallet();
      checkServiceStatus();
    } else {
      // Clear state when user logs out
      setWallet(null);
      setBalance(null);
      setTransactions([]);
      setServiceStatus(null);
    }
  }, [user]);

  // Auto-refresh balance when wallet changes
  useEffect(() => {
    if (wallet) {
      refreshBalance();
      loadTransactions();
    }
  }, [wallet]);

  const loadWallet = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await platformApi.ethereum.getWallet();
      
      if (response.hasWallet && response.wallet) {
        setWallet(response.wallet);
        
        // Load balance if wallet has balance info
        if (response.wallet.balance) {
          setBalance(response.wallet.balance);
        }
      }
    } catch (err: any) {
      console.error('Failed to load wallet:', err);
      setError(err.message || 'Failed to load Ethereum wallet');
    } finally {
      setLoading(false);
    }
  };

  const createWallet = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newWallet = await platformApi.ethereum.createWallet();
      setWallet(newWallet);
      
      toast({
        title: 'Wallet Created',
        description: 'Your Ethereum wallet has been created successfully',
      });
    } catch (err: any) {
      console.error('Failed to create wallet:', err);
      setError(err.message || 'Failed to create wallet');
      toast({
        title: 'Error',
        description: err.message || 'Failed to create wallet',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const importWallet = async (data: { privateKey?: string; mnemonic?: string }) => {
    try {
      setLoading(true);
      setError(null);
      
      const importedWallet = await platformApi.ethereum.importWallet(data);
      setWallet(importedWallet);
      
      toast({
        title: 'Wallet Imported',
        description: 'Your Ethereum wallet has been imported successfully',
      });
    } catch (err: any) {
      console.error('Failed to import wallet:', err);
      setError(err.message || 'Failed to import wallet');
      toast({
        title: 'Error',
        description: err.message || 'Failed to import wallet',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshBalance = async () => {
    try {
      const balanceData = await platformApi.ethereum.getBalance();
      setBalance({
        balanceEth: balanceData.balanceEth,
        balanceUsd: balanceData.balanceUsd,
        ethPrice: balanceData.ethPrice,
      });
    } catch (err: any) {
      console.error('Failed to refresh balance:', err);
      // Don't show error toast for balance refresh failures
    }
  };

  const loadTransactions = async (limit = 50, skip = 0) => {
    try {
      const response = await platformApi.ethereum.getTransactionHistory(limit, skip);
      
      if (skip === 0) {
        setTransactions(response.transactions);
      } else {
        // Append for pagination
        setTransactions(prev => [...prev, ...response.transactions]);
      }
    } catch (err: any) {
      console.error('Failed to load transactions:', err);
      // Don't show error toast for transaction loading failures
    }
  };

  const checkServiceStatus = async () => {
    try {
      const status = await platformApi.ethereum.getServiceStatus();
      setServiceStatus(status);
    } catch (err: any) {
      console.error('Failed to check service status:', err);
      // Service might not be enabled, don't show error
    }
  };

  return (
    <EthereumContext.Provider
      value={{
        wallet,
        balance,
        transactions,
        serviceStatus,
        loading,
        error,
        createWallet,
        importWallet,
        loadWallet,
        refreshBalance,
        loadTransactions,
        checkServiceStatus,
      }}
    >
      {children}
    </EthereumContext.Provider>
  );
}
